import { Suspense } from "react";
import type { ReactElement } from "react";
import {
  Route as RouteV6,
  Routes as BaseRoutes,
  Navigate,
} from "react-router-dom";
import {
  AsyncProfile,
  AsyncPageNotFound,
  AsyncAml,
  AsyncAmlDetails,
  AsyncCreateProfile,
  AsyncProfileExport,
  AsyncGlobalAudits,
  AsyncMonitor,
  AsyncAddAmlSearchToProfile,
  AsyncUploads,
  AsyncReports,
  AsyncSettings,
  AsyncAmlCheck,
} from "./LoadableRoutes";
import { Loading } from "@creditsafe/ui-components";
import { ThemeProvider } from "styled-components";
import { theme } from "../assets/theme";
import ProfileListPage from "../layout/ProfileListPage";
import ServiceLineCheckWrapper from "./ServiceLineCheckWrapper";

interface RouteProps {
  isUpp: boolean | undefined;
}

const Routes = ({ isUpp }: RouteProps): ReactElement => {
  return (
    <ServiceLineCheckWrapper isUpp={isUpp}>
      <Suspense
        fallback={
          <ThemeProvider theme={theme}>
            <Loading
              className=""
              text="Loading"
              dotsCount={5}
              fadeInPercentage={66}
              delayMilliseconds={500}
            />
          </ThemeProvider>
        }
      >
        <BaseRoutes>
          {/* As we are using react RouterV6 and Upp is using React Router version 6 while exposing the app to upp we are facing conflicts, to avoid that we need to alias the Route to RouteV6*/}
          <RouteV6 path="/" element={<Navigate to="/protect" />} />
          <RouteV6 path="/callback" element={<Navigate to="/protect" />} />
          <RouteV6 path="/protect" element={<ProfileListPage />} />
          <RouteV6
            path="protect/profile/:profileId"
            element={<AsyncProfile />}
          />
          <RouteV6
            path="protect/profile/:profileId/:tab"
            element={<AsyncProfile />}
          />
          <RouteV6
            path="protect/profile/:profileId/:tab/:subTab"
            element={<AsyncProfile />}
          />
          <RouteV6 path="protect/aml-search" element={<AsyncAml />} />
          <RouteV6
            path="protect/amlDetails/:profileType/:searchId/:hitId"
            element={<AsyncAmlDetails />}
          />
          <RouteV6
            path="protect/create-profile"
            element={<AsyncCreateProfile />}
          />
          <RouteV6
            path="protect/profile-export"
            element={<AsyncProfileExport />}
          />
          <RouteV6
            path="protect/aml-details/:profileType/:searchId/:hitId/:tab?"
            element={<AsyncAmlDetails />}
          />
          <RouteV6
            path="protect/aml-details/:profileType/:searchId/:hitId/:tab/:subTab?"
            element={<AsyncAmlDetails />}
          />
          <RouteV6
            path="protect/add-aml-search-to-profile"
            element={<AsyncAddAmlSearchToProfile />}
          />
          <RouteV6 path="protect/audits" element={<AsyncGlobalAudits />} />

          {/* Below route is added to handle the redirect from the old URL to the new URL we need to change this once UPP updated the route */}
          <RouteV6
            path="/protect/alerts"
            element={<Navigate to="/protect/alerts/alerts-dashboard" />}
          />
          <RouteV6
            path="protect/alerts/:monitoringTab"
            element={<AsyncMonitor />}
          />
          <RouteV6 path="protect/uploads" element={<AsyncUploads />} />
          <RouteV6 path="*" element={<AsyncPageNotFound />} />
          <RouteV6
            path="/protect/pageNotFound"
            element={<AsyncPageNotFound />}
          />
          <RouteV6
            path="protect/reports/:reportTab"
            element={<AsyncReports />}
          />
          <RouteV6
            path="protect/aml-details/search-links/:profileType/:supplierHitId/:tab?"
            element={<AsyncAmlDetails />}
          />
          <RouteV6
            path="protect/aml-details/search-links/:profileType/:supplierHitId/:tab/:subTab?"
            element={<AsyncAmlDetails />}
          />
          <RouteV6 path="protect/settings" element={<AsyncSettings />} />
          <RouteV6 path="protect/add-to-profile" element={<AsyncAmlCheck />} />
        </BaseRoutes>
      </Suspense>
    </ServiceLineCheckWrapper>
  );
};

export default Routes;
